"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = (props: SheetPrimitive.DialogPortalProps) => (
  <SheetPrimitive.Portal {...props} />
)
SheetPortal.displayName = SheetPrimitive.Portal.displayName

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-background/80 backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-0 shadow-lg transition ease-in-out" +
  " data-[state=open]:animate-in data-[state=closed]:animate-out" +
  " data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b" +
             " data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t" +
          " data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full border-r" +
              " data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        right:
          "inset-y-0 right-0 h-full border-l" +
          " data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
      },
      size: {
        content: "",
        default: "",
        sm: "",
        lg: "",
        xl: "",
        full: "",
      },
    },
    compoundVariants: [
      {
        side: "top",
        size: "content",
        class: "max-h-screen",
      },
      {
        side: "top",
        size: "default",
        class: "h-1/3",
      },
      {
        side: "top",
        size: "sm",
        class: "h-1/4",
      },
      {
        side: "top",
        size: "lg",
        class: "h-1/2",
      },
      {
        side: "top",
        size: "xl",
        class: "h-5/6",
      },
      {
        side: "top",
        size: "full",
        class: "h-screen",
      },
      {
        side: "bottom",
        size: "content",
        class: "max-h-screen",
      },
      {
        side: "bottom",
        size: "default",
        class: "h-1/3",
      },
      {
        side: "bottom",
        size: "sm",
        class: "h-1/4",
      },
      {
        side: "bottom",
        size: "lg",
        class: "h-1/2",
      },
      {
        side: "bottom",
        size: "xl",
        class: "h-5/6",
      },
      {
        side: "bottom",
        size: "full",
        class: "h-screen",
      },
      {
        side: "right",
        size: "content",
        class: "max-w-screen",
      },
      {
        side: "right",
        size: "default",
        class: "w-1/3",
      },
      {
        side: "right",
        size: "sm",
        class: "w-1/4",
      },
      {
        side: "right",
        size: "lg",
        class: "w-1/2",
      },
      {
        side: "right",
        size: "xl",
        class: "w-5/6",
      },
      {
        side: "right",
        size: "full",
        class: "w-screen",
      },
      {
        side: "left",
        size: "content",
        class: "max-w-screen",
      },
      {
        side: "left",
        size: "default",
        class: "w-[312px]", // 72px(서버) + 240px(채널) = 312px
      },
      {
        side: "left",
        size: "sm",
        class: "w-1/4",
      },
      {
        side: "left",
        size: "lg",
        class: "w-1/2",
      },
      {
        side: "left",
        size: "xl",
        class: "w-5/6",
      },
      {
        side: "left",
        size: "full",
        class: "w-screen",
      },
    ],
    defaultVariants: {
      side: "right",
      size: "default",
    },
  }
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  showCloseButton?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", size = "default", className, children, showCloseButton = true, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side, size }), className)}
      {...props}
    >
      <SheetTitle className="sr-only">시트 제목</SheetTitle>
      {children}
      {showCloseButton && (
        <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
          <X className="h-4 w-4" />
          <span className="sr-only">닫기</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
